/** Simple internship capacity what-if (matches clinic partner-policy math). */

export type CapacityInputs = {
  /** Seats available in one slot (capacity / slot). */
  capacityPerSlot: number;
  /** Slots per day (operating hours ÷ 4). */
  slotsPerDay: number;
  /** Physical / real calendar weeks in the planning window. */
  physicalWeeks: number;
  /** How many branches run this pattern. */
  branches: number;
  /** Training weeks counted per physical week. */
  trainingWeeksPerWeek: number;
};

export type CapacityResult = {
  /** capacity × slots/day × branches × training-weeks/week */
  weeklyInternWeeks: number;
  /** weekly × physical weeks */
  totalInternWeeks: number;
};

export const DEFAULT_CAPACITY_INPUTS: CapacityInputs = {
  capacityPerSlot: 4,
  slotsPerDay: 1,
  physicalWeeks: 26,
  branches: 5,
  trainingWeeksPerWeek: 1,
};

const STORAGE_KEY = 'physioedge.admin.capacityCalculator.v3';

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

export function normalizeCapacityInputs(raw: Partial<CapacityInputs>): CapacityInputs {
  return {
    capacityPerSlot: clamp(
      Math.round(Number(raw.capacityPerSlot) || DEFAULT_CAPACITY_INPUTS.capacityPerSlot),
      1,
      50,
    ),
    slotsPerDay: clamp(
      Math.round(Number(raw.slotsPerDay) || DEFAULT_CAPACITY_INPUTS.slotsPerDay),
      1,
      20,
    ),
    physicalWeeks: clamp(
      Math.round(Number(raw.physicalWeeks) || DEFAULT_CAPACITY_INPUTS.physicalWeeks),
      1,
      104,
    ),
    branches: clamp(Math.round(Number(raw.branches) || DEFAULT_CAPACITY_INPUTS.branches), 1, 100),
    trainingWeeksPerWeek: clamp(
      Math.round(Number(raw.trainingWeeksPerWeek) || DEFAULT_CAPACITY_INPUTS.trainingWeeksPerWeek),
      1,
      20,
    ),
  };
}

/**
 * Total = capacity/slot × slots/day × physical weeks × branches × training weeks/week
 */
export function calcCapacity(raw: Partial<CapacityInputs>): CapacityResult {
  const inputs = normalizeCapacityInputs(raw);
  const weeklyInternWeeks =
    inputs.capacityPerSlot * inputs.slotsPerDay * inputs.branches * inputs.trainingWeeksPerWeek;
  const totalInternWeeks = weeklyInternWeeks * inputs.physicalWeeks;
  return { weeklyInternWeeks, totalInternWeeks };
}

export function loadCapacityInputs(): CapacityInputs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_CAPACITY_INPUTS };
    return normalizeCapacityInputs(JSON.parse(raw) as Partial<CapacityInputs>);
  } catch {
    return { ...DEFAULT_CAPACITY_INPUTS };
  }
}

export function saveCapacityInputs(inputs: CapacityInputs): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizeCapacityInputs(inputs)));
  } catch {
    // ignore quota / private mode
  }
}
